import { useCallback } from 'react'
import { cn } from '@/lib/utils'
import type { Candidate } from '@/types'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'

// ============================================================================
// Types
// ============================================================================

interface CandidateMapSelectorProps {
  candidates: Candidate[]
  selectedIds: string[]
  onSelectionChange: (ids: string[]) => void
  partyLeaderId?: string
  className?: string
}

// ============================================================================
// Component
// ============================================================================

export function CandidateMapSelector({
  candidates,
  selectedIds,
  onSelectionChange,
  partyLeaderId,
  className,
}: CandidateMapSelectorProps) {
  const allSelected = candidates.length > 0 && selectedIds.length === candidates.length
  const noneSelected = selectedIds.length === 0
  const partyLeader = candidates.find(c => c.id === partyLeaderId)

  const handleSelectAll = useCallback(() => {
    if (allSelected) {
      onSelectionChange([])
    } else {
      onSelectionChange(candidates.map(c => c.id))
    }
  }, [allSelected, candidates, onSelectionChange])

  const handleSelectLeaderOnly = useCallback(() => {
    if (partyLeaderId) {
      onSelectionChange([partyLeaderId])
    }
  }, [partyLeaderId, onSelectionChange])

  const handleClearAll = useCallback(() => {
    onSelectionChange([])
  }, [onSelectionChange])

  const handleToggleCandidate = useCallback((candidateId: string, checked: boolean) => {
    if (checked) {
      onSelectionChange([...selectedIds, candidateId])
    } else {
      onSelectionChange(selectedIds.filter(id => id !== candidateId))
    }
  }, [selectedIds, onSelectionChange])

  if (candidates.length === 0) {
    return (
      <div className={cn('text-sm text-muted-foreground p-4 text-center', className)}>
        No candidates available
      </div>
    )
  }

  return (
    <div className={cn('space-y-3', className)}>
      {/* Quick actions */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant={allSelected ? 'default' : 'outline'}
          size="sm"
          onClick={handleSelectAll}
        >
          {allSelected ? 'Deselect All' : 'All Candidates'}
        </Button>

        {partyLeader && (
          <Button
            variant={selectedIds.length === 1 && selectedIds[0] === partyLeaderId ? 'default' : 'outline'}
            size="sm"
            onClick={handleSelectLeaderOnly}
          >
            Leader Only
          </Button>
        )}

        {!noneSelected && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearAll}
          >
            Clear
          </Button>
        )}

        <span className="ml-auto text-xs text-muted-foreground self-center">
          {selectedIds.length} of {candidates.length} selected
        </span>
      </div>

      {/* Candidate list */}
      <div className="space-y-1 max-h-60 overflow-y-auto pr-1">
        {candidates.map(candidate => {
          const isSelected = selectedIds.includes(candidate.id)
          const isLeader = candidate.id === partyLeaderId

          return (
            <label
              key={candidate.id}
              className={cn(
                'flex items-center gap-3 px-2 py-1.5 rounded cursor-pointer',
                'hover:bg-muted/50 transition-colors',
                isSelected && 'bg-muted/30'
              )}
            >
              <Checkbox
                checked={isSelected}
                onCheckedChange={(checked) => handleToggleCandidate(candidate.id, checked)}
              />

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={cn(
                    'text-sm truncate',
                    isLeader && 'font-medium'
                  )}>
                    {candidate.name}
                  </span>
                  {isLeader && (
                    <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-primary/10 text-primary">
                      Leader
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">
                  {getRoleLabel(candidate.role)}
                  {candidate.recognition > 70 && ' • Well-known'}
                  {candidate.loyalty < 50 && ' • Low loyalty'}
                </div>
              </div>

              {/* Loyalty indicator */}
              <div className={cn(
                'w-2 h-2 rounded-full flex-shrink-0',
                candidate.loyalty >= 70 && 'bg-green-500',
                candidate.loyalty >= 40 && candidate.loyalty < 70 && 'bg-yellow-500',
                candidate.loyalty < 40 && 'bg-red-500'
              )}
              title={`Loyalty: ${candidate.loyalty}%`}
              />
            </label>
          )
        })}
      </div>
    </div>
  )
}

// ============================================================================
// Helpers
// ============================================================================

function getRoleLabel(role: Candidate['role']): string {
  const roleLabels: Record<Candidate['role'], string> = {
    none: 'Unassigned',
    regional_candidate: 'Regional Candidate',
    list_candidate: 'List Candidate',
    party_leader: 'Party Leader',
    minister: 'Minister',
    chair: 'Chair',
  }
  return roleLabels[role] || role
}

// ============================================================================
// Compact Selector Variant
// ============================================================================

interface CandidateMapSelectorCompactProps {
  candidates: Candidate[]
  selectedIds: string[]
  onSelectionChange: (ids: string[]) => void
  partyLeaderId?: string
}

export function CandidateMapSelectorCompact({
  candidates,
  selectedIds,
  onSelectionChange,
  partyLeaderId,
}: CandidateMapSelectorCompactProps) {
  const handleSelectAll = () => {
    if (selectedIds.length === candidates.length) {
      onSelectionChange([])
    } else {
      onSelectionChange(candidates.map(c => c.id))
    }
  }

  const handleSelectLeader = () => {
    if (partyLeaderId) {
      onSelectionChange([partyLeaderId])
    }
  }

  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="text-muted-foreground">Show:</span>
      <Button
        variant={selectedIds.length === candidates.length ? 'default' : 'outline'}
        size="sm"
        onClick={handleSelectAll}
      >
        All ({candidates.length})
      </Button>
      {partyLeaderId && (
        <Button
          variant={selectedIds.length === 1 && selectedIds[0] === partyLeaderId ? 'default' : 'outline'}
          size="sm"
          onClick={handleSelectLeader}
        >
          Leader
        </Button>
      )}
      {selectedIds.length > 0 && selectedIds.length < candidates.length && (
        <span className="text-xs text-muted-foreground">
          {selectedIds.length} selected
        </span>
      )}
    </div>
  )
}
